import React from 'react';
import dataCardStyles from '../DataCard/DataCard.module.css';
import styles from './DataTable.module.css';

/* ── Highlight context ── */

const HighlightContext = React.createContext<RegExp | null>(null);
const HeaderContext = React.createContext<string[]>([]);
const LastColumnActionsContext = React.createContext(true);

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Folds a single character to its accent-stripped, lowercase form while keeping
// a stable per-character mapping (accented latin letters fold to one base letter).
function foldChar(char: string): string {
  return char.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function foldText(value: string): string {
  return Array.from(value).map(foldChar).join('');
}

function highlightString(value: string, regex: RegExp): React.ReactNode {
  const originalChars = Array.from(value);

  // Build an accent-folded version of the string with a folded-index -> original
  // char-index map, so we can match ignoring accents but highlight the original text.
  let folded = '';
  const foldedToOriginal: number[] = [];

  originalChars.forEach((char, originalIndex) => {
    const foldedChar = foldChar(char);
    for (let i = 0; i < foldedChar.length; i += 1) {
      foldedToOriginal.push(originalIndex);
    }
    folded += foldedChar;
  });

  const nodes: React.ReactNode[] = [];
  let lastOriginalIndex = 0;
  let matchIndex = 0;
  let match: RegExpExecArray | null;

  regex.lastIndex = 0;
  while ((match = regex.exec(folded)) !== null) {
    if (match[0].length === 0) {
      regex.lastIndex += 1;
      continue;
    }

    const foldedStart = match.index;
    const foldedEnd = match.index + match[0].length;
    const originalStart = foldedToOriginal[foldedStart];
    const originalEnd = foldedEnd < foldedToOriginal.length
      ? foldedToOriginal[foldedEnd]
      : originalChars.length;

    if (originalStart > lastOriginalIndex) {
      nodes.push(originalChars.slice(lastOriginalIndex, originalStart).join(''));
    }

    nodes.push(
      <mark key={`m-${matchIndex}`} className={styles['highlight-match']}>
        {originalChars.slice(originalStart, originalEnd).join('')}
      </mark>,
    );

    lastOriginalIndex = originalEnd;
    matchIndex += 1;
  }

  if (matchIndex === 0) return value;

  if (lastOriginalIndex < originalChars.length) {
    nodes.push(originalChars.slice(lastOriginalIndex).join(''));
  }

  return nodes;
}

/** Pasted mega-queries explode into catastrophic char[.-]*char patterns. */
const MAX_HIGHLIGHT_PATTERN_CHARS = 48;

function buildHighlightRegex(term: string): RegExp | null {
  const stripped = foldText(term).replace(/[.-]/g, '');
  if (!stripped || stripped.length > MAX_HIGHLIGHT_PATTERN_CHARS) {
    return null;
  }

  try {
    const pattern = stripped.split('').map(escapeRegex).join('[.-]*');
    return new RegExp(pattern, 'gi');
  } catch {
    return null;
  }
}

function highlightChildren(children: React.ReactNode, regex: RegExp): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return highlightString(child, regex);
    }

    if (React.isValidElement<{ children?: React.ReactNode }>(child) && child.props.children) {
      return React.cloneElement(child, {}, highlightChildren(child.props.children, regex));
    }

    return child;
  });
}

/* ── Row sub-component ── */

interface RowProps {
  children: React.ReactNode;
  highlight?: boolean;
  /** Light-red row background while the student has an open sensitive period. */
  sensitive?: boolean;
  /** Applies a primary-color border accent (e.g. first contact registered). */
  accentBorder?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const Row: React.FC<RowProps> = ({
  children,
  highlight,
  sensitive,
  accentBorder,
  className = '',
  style,
}) => {
  const highlightRegex = React.useContext(HighlightContext);
  const headers = React.useContext(HeaderContext);
  const lastColumnIsActions = React.useContext(LastColumnActionsContext);

  const cls = [
    dataCardStyles.card,
    styles['row-card'],
    highlight ? styles['row-highlight'] : '',
    sensitive ? styles['row-sensitive'] : '',
    accentBorder ? styles['row-accent-border'] : '',
    className,
  ].filter(Boolean).join(' ');

  const labeledChildren = React.Children.toArray(children)
    .filter((child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child).trim() !== '';
      }
      return true;
    })
    .map((child, index) => {
      const label = headers[index] ?? '';
      const isActionsCell = lastColumnIsActions && index === headers.length - 1;
      const existingLabel = React.isValidElement<{ 'data-card-label'?: string }>(child)
        ? child.props['data-card-label']
        : undefined;
      const cardProps = {
        'data-card-label': existingLabel !== undefined ? existingLabel : label,
        'data-card-actions': isActionsCell ? 'true' : undefined,
      };

      if (React.isValidElement<Record<string, unknown>>(child)) {
        return React.cloneElement(child, cardProps);
      }

      return <span {...cardProps}>{child}</span>;
    });

  return (
    <div className={cls} style={style}>
      {highlightRegex ? highlightChildren(labeledChildren, highlightRegex) : labeledChildren}
    </div>
  );
};

/* ── DataTable ── */

interface DataTableProps {
  headers: React.ReactNode[];
  gridTemplate?: string;
  children: React.ReactNode;
  className?: string;
  highlightTerm?: string;
  lastColumnIsActions?: boolean;
  subgrid?: boolean;
  /** Shows a visible row border even without hover (same as payment history in student details). */
  borderedRows?: boolean;
  /** Uses bold column headers (same as payment details labels in student profile). */
  boldHeaders?: boolean;
}

const DataTable: React.FC<DataTableProps> & { Row: typeof Row } = ({
  headers,
  gridTemplate,
  children,
  className = '',
  highlightTerm = '',
  lastColumnIsActions = true,
  subgrid = false,
  borderedRows = false,
  boldHeaders = false,
}) => {
  const cssVars = gridTemplate ? ({ '--dt-grid': gridTemplate } as React.CSSProperties) : undefined;
  const headerLabels = headers.map((header) => {
    if (typeof header === 'string' || typeof header === 'number') {
      return String(header);
    }

    if (React.isValidElement<{ children?: React.ReactNode; 'data-header-label'?: string }>(header)) {
      if (typeof header.props['data-header-label'] === 'string') {
        return header.props['data-header-label'];
      }

      const child = header.props.children;
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child);
      }
    }

    return '';
  });

  const tableBody = (
    <>
      <div className={styles['list-header']}>
        {headers.map((header, index) => {
          const isActionsHeader = lastColumnIsActions && index === headers.length - 1;
          const actionsHeaderClass = isActionsHeader ? styles['actions-header'] : undefined;

          if (React.isValidElement<{ className?: string }>(header)) {
            return React.cloneElement(header, {
              key: index,
              className: [header.props.className, actionsHeaderClass].filter(Boolean).join(' '),
            });
          }

          return (
            <span key={index} className={actionsHeaderClass}>
              {header}
            </span>
          );
        })}
      </div>
      <div className={styles['rows-container']}>
        {children}
      </div>
    </>
  );

  return (
    <HighlightContext.Provider value={buildHighlightRegex(highlightTerm.trim())}>
      <LastColumnActionsContext.Provider value={lastColumnIsActions}>
        <HeaderContext.Provider value={headerLabels}>
          <div
            className={[
              styles.container,
              subgrid ? styles['subgrid-table'] : '',
              borderedRows ? styles['bordered-rows'] : '',
              boldHeaders ? styles['bold-headers'] : '',
              className,
            ].filter(Boolean).join(' ')}
            style={cssVars}
          >
            {subgrid ? (
              <div className={styles['table-grid']}>
                {tableBody}
              </div>
            ) : (
              tableBody
            )}
          </div>
        </HeaderContext.Provider>
      </LastColumnActionsContext.Provider>
    </HighlightContext.Provider>
  );
};

DataTable.Row = Row;

export default DataTable;
